import { Button, Drawer, Form, Input } from "antd"
import type { FormInstance } from "antd"
import { XMarkIcon } from "@heroicons/react/24/outline"
import CustomInput from "./CustomInput"
import {
  toSelectOptions,
  calculateRadius,
  getAddressComponent,
  matchState,
} from "../../utils/shiftTablesUtils"
import type { OfficeOption } from "./SetupWorkplaceSelection"
import { useQuery } from "@tanstack/react-query"
import { COMMON_ENDPOINTS, commonService } from "services/commonService"
import type { IState } from "pages/dashboard/people/people/typings"
import GeoPicker from "components/GeoPicker"
import { WorkplaceFormValues } from "../../typings"

interface Props {
  open: boolean
  isEditingAddress: boolean
  onClose: () => void
  form: FormInstance<WorkplaceFormValues>
  onFinish: (values: WorkplaceFormValues) => void
  onValuesChange: (
    changed: Partial<WorkplaceFormValues>,
    all: WorkplaceFormValues
  ) => void
  taxOffices: OfficeOption[]
  isTaxOfficesLoading: boolean
  housingLocations: OfficeOption[]
  isHousingLocationsLoading: boolean
  isCreatingWorkplace: boolean
  isUpdatingWorkplace: boolean
  alpha2Code: string
}

const WorkplaceFormDrawer: React.FC<Props> = ({
  open,
  isEditingAddress,
  onClose,
  form,
  onFinish,
  onValuesChange,
  taxOffices,
  isTaxOfficesLoading,
  housingLocations,
  isHousingLocationsLoading,
  isCreatingWorkplace,
  isUpdatingWorkplace,
  alpha2Code,
}) => {
  const { data: statesRes } = useQuery<IBaseRes<IState[]>>({
    queryKey: [COMMON_ENDPOINTS.GET_STATES_BY_COUNTRY, alpha2Code],
    queryFn: () => commonService.getStatesByCountry(alpha2Code),
    enabled: !!alpha2Code,
    refetchOnWindowFocus: false,
    cacheTime: 24 * 60 * 60 * 1000,
    keepPreviousData: true,
  })
  const states = statesRes?.data

  const latitude = Form.useWatch("latitude", form)
  const longitude = Form.useWatch("longitude", form)
  const isMissingCoordinates =
    latitude == null || longitude == null || latitude === "" || longitude === ""

  const handlePlaceDetails = (place: any) => {
    try {
      const comps = place.address_components ?? []
      const countryComp = getAddressComponent(comps, "country")
      const stateComp = getAddressComponent(
        comps,
        "administrative_area_level_1"
      )
      const countryCode = countryComp?.short_name ?? ""
      const stateName = stateComp?.long_name ?? stateComp?.short_name ?? ""
      const stateShortName = stateComp?.short_name ?? ""
      const stateValue = matchState(states ?? [], stateName, stateShortName)
      const currentValues = form.getFieldsValue()
      form.setFieldsValue({ country: countryCode, state: stateValue })
      onValuesChange(
        { country: countryCode },
        { ...currentValues, country: countryCode }
      )
      onValuesChange(
        { state: stateValue },
        { ...currentValues, state: stateValue }
      )
    } catch {}
  }

  return (
    <Drawer
      rootClassName="ta-scope"
      closeIcon={null}
      styles={{
        header: { padding: 16 },
        body: { padding: 16 },
        footer: { padding: 16 },
      }}
      title={
        <div className="flex items-start justify-between w-full">
          <div className="text-slate-900">
            <span className="font-bold text-lg">
              {isEditingAddress ? "Edit workplace" : "Create workplace"}
            </span>
            <p className="text-sm">
              {isEditingAddress
                ? "Update this workplace's address below."
                : "Please enter your workplace's details below."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-4 -m-1 rounded text-[#64748B] hover:text-[#0F172B] hover:bg-[#F1F5F9]"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 5.00004L5 15M4.99996 5L14.9999 15"
                stroke="#0F172B"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      }
      width={564}
      open={open}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-3">
          <Button
            type="default"
            className="font-semibold h-10 px-4 rounded-md border-2 border-[#E2E8F0] text-[#0F172B]"
            onClick={onClose}
            disabled={isCreatingWorkplace || isUpdatingWorkplace}
          >
            Cancel
          </Button>

          <Button
            type="primary"
            className="font-semibold h-10 px-4 rounded-md bg-[#004AF5]"
            loading={
              isEditingAddress ? isUpdatingWorkplace : isCreatingWorkplace
            }
            disabled={
              isCreatingWorkplace ||
              isUpdatingWorkplace ||
              (isEditingAddress && isMissingCoordinates)
            }
            onClick={() => form.submit()}
          >
            {isEditingAddress ? "Save workplace detail" : "Create workplace"}
          </Button>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onValuesChange={onValuesChange}
      >
        <CustomInput
          label="Workplace name"
          name="workplaceName"
          control="input"
          placeholder="Enter workplace name"
          rules={[{ required: true, message: "Please enter workplace name" }]}
        />
        <Form.Item
          label="Workplace primary address"
          name="primaryAddress"
          rules={[
            { required: true, message: "Please enter workplace's address" },
          ]}
        >
          <GeoPicker
            value={form.getFieldValue("primaryAddress")}
            onChange={(address) =>
              form.setFieldsValue({ primaryAddress: address })
            }
            onSelect={(lat, lng) => {
              const radius = calculateRadius(lat, lng)
              form.setFieldsValue({ latitude: lat, longitude: lng, radius })
            }}
            onPlaceDetails={handlePlaceDetails}
          />
        </Form.Item>
        <Form.Item
          name="latitude"
          hidden
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="longitude"
          hidden
        >
          <Input />
        </Form.Item>
        <CustomInput
          label="Tax office [optional]"
          name="taxOffice"
          control="select"
          placeholder="Select tax office"
          allowClear
          selectLoading={isTaxOfficesLoading}
          options={isTaxOfficesLoading ? [] : toSelectOptions(taxOffices ?? [])}
        />
        <Form.Item
          name="taxOfficeCode"
          noStyle
        >
          <Input type="hidden" />
        </Form.Item>
        <CustomInput
          label="NHF office [optional]"
          name="nhfOffice"
          control="select"
          placeholder="Select NHF office"
          allowClear
          selectLoading={isHousingLocationsLoading}
          options={
            isHousingLocationsLoading
              ? []
              : toSelectOptions(housingLocations ?? [])
          }
        />
        <Form.Item
          name="nhfOfficeCode"
          noStyle
        >
          <Input type="hidden" />
        </Form.Item>
      </Form>
    </Drawer>
  )
}

export default WorkplaceFormDrawer
